import React, {
 useState,useContext
} from 'react';
import {
 Database, Trash2, Edit, Zap, Settings
} from 'lucide-react';
import {
  TooltipProvider
} from 'shared/components/ui/tooltip';
import LoadingSpinner from './LoadingSpinner';
import {
 Connection
} from './Types/ConnectionTypes';
import {
 connectionService
} from 'shared/api/services/BEX';
import {
 Context
} from 'shared/context/GlobalState';

interface ConnectionCardProps {
    connection: Connection;
    onEdit: (connection: Connection) => void;
    setModelProps?: React.Dispatch<React.SetStateAction<{ open: boolean; data: Connection }>>;
    selected?: boolean;
    onSelect?: (connection_id: string, checked: boolean) => void;
}

const ConnectionCard: React.FC<ConnectionCardProps> = ({
    connection,
    onEdit,
    setModelProps,
    selected = false,
    onSelect
}) => {
    const [testingConnections, setTestingConnections] = useState<Set<string>>(new Set());
     const {
     addNewNotifcation
    } = useContext(Context);

    const handleTestConnection = async (connection: Connection) => {
    const connectionId = connection?.connection_id;
    const orgId = connection?.orgId;
    const appId = connection?.appId;

    if (!connectionId || !orgId || !appId) return;

    // Add connectionId to the testing set
    setTestingConnections((prev) => new Set(prev).add(connectionId));

    try {
        // Call the testSingleConnection API
        await connectionService.testSingleConnection(orgId, appId, connectionId);

        // Handle the result if necessary

        // Optionally add a success notification or update state based on result
        addNewNotifcation('Connection tested successfully', 'success');
    } catch (error) {
        // Handle error from API call
        console.error('Test connection failed:', error);
        // Optionally add an error notification
        addNewNotifcation('Test connection failed. Please try again later.', 'error');
    } finally {
        // Remove connectionId from the testing set after the operation completes
        setTestingConnections((prev) => {
            const newSet = new Set(prev);
            newSet.delete(connectionId);
            return newSet;
        });
    }
};

    const [isHovered, setIsHovered] = useState(false);

    // Show all checkboxes if any card is selected
    const showCheckbox = selected || isHovered;
    return (
        <TooltipProvider>
            <div
                key={connection?.connection_id}
                className="bg-white/90 dark:bg-neutral-900 backdrop-blur-sm rounded-2xl border border-white/30 p-6 md:p-8 shadow-lg hover:shadow-xl hover:border-white/50 transition-all duration-300 transform hover:-translate-y-1 space-y-4 relative"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Bulk select checkbox (top-right, visible if selected or hovered) */}
                <div className="absolute top-4 right-4 z-10">
                    <input
                        type="checkbox"
                        className={`w-5 h-5 accent-blue-600 cursor-pointer transition-opacity duration-200 ${showCheckbox ? 'opacity-100' : 'opacity-0'}`}
                        checked={selected}
                        disabled={!showCheckbox}
                        onChange={e => onSelect && onSelect(connection.connectionId || connection.connection_id, e.target.checked)}
                    />
                </div>
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className={'p-2 rounded-lg bg-gray-100'}>
                            {connection?.source_type === 'rest_api' ? (
                                <Settings className={'w-5 h-5 text-gray-800'} />
                            ) : (
                                <Database className={'w-5 h-5 text-gray-800'} />
                            )}
                        </div>
                        <span
                            className={'inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'}
                        >
                            {connection?.source_type}
                        </span>
                    </div>
                </div>

                {/* Name + Description */}
                <div className="text-left space-y-1">
                    {connection?.connection_name && (
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {connection?.connection_name}
                        </h3>
                    )}
                    {connection?.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                            {connection?.description}
                        </p>
                    )}
                </div>

                {/* Connection Info */}
                {connection?.source_type === 'rest_api' ? (
                    <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <span className="font-medium">Url:</span>
                            <span className="truncate">{`${connection?.settings?.base_url}:${connection?.settings?.path}`}</span>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                            <Database className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <span className="font-medium">Host:</span>
                            <span className="truncate">{`${connection?.settings?.host}:${connection?.settings?.port}`}</span>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-neutral-800">
                    <div className="flex items-center gap-2">
                        {/* Test */}
                        {/* <Tooltip content="Test connection to verify connectivity"> */}
                        <button
                                onClick={() => handleTestConnection(connection)}
                                disabled={testingConnections.has(connection?.connection_id)}
                                className="p-2.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-xl transition-all duration-200 disabled:opacity-50 hover:scale-105 active:scale-95 shadow-sm"
                            >
                            {testingConnections.has(connection?.connection_id) ? (
                                <LoadingSpinner size="lg" />
                                ) : (
                                    <Zap className="w-4 h-4" />
                                )}
                        </button>
                        {/* </Tooltip> */}

                        {/* Edit */}
                        {/* <Tooltip content="Edit connection settings"> */}
                        <button
                                onClick={() => onEdit(connection)}
                                className="p-2.5 text-gray-600 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 shadow-sm"
                            >
                            <Edit className="w-4 h-4" />
                        </button>
                        {/* </Tooltip> */}
                    </div>

                    {/* Delete */}
                    <button
                            onClick={() => setModelProps({
                                open: true,
                                data: connection
                            })}
                            className="p-2.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 shadow-sm"
                        >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </TooltipProvider>
    );
};

export default ConnectionCard;
